import { useMemo } from 'react'
import PropTypes from 'prop-types'
import UserAvatar from 'react-user-avatar'
import { Media, Badge } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown } from '@fortawesome/free-solid-svg-icons/faChevronDown'

import ConditionalLink from '~/components/shared/ConditionalLink'
import AvatarAlias from '~/components/shared/AvatarAlias'
import withLocale from '~/hocs/locale'
import { EmailPropType, MessagePropType } from '~/utils/prop-types'
import { formatISOdate } from '~/utils/format'
import routes from '~/routes'

const UserMedia = ({
  value,
  size,
  anonymousName,
  opposedTag,
  fromLabel,
  slug,
  asAnonymous,
  isAdmin,
  isRelated,
  isSearchSection,
}) => {
  const { fromRedacted, author, user, showAuthor, date, createdAt } = value
  const owner = author || user
  const createdDate = date || createdAt
  let Avatar = null
  let isAnonymous = false
  let linkProps = {}
  let name

  if (fromRedacted) {
    name = fromRedacted
    linkProps = {
      href: '/organizations/[slug]',
      as: `/organizations/${slug}?text=${name}`,
    }

    Avatar = <AvatarAlias name={fromRedacted} />
  } else {
    isAnonymous = asAnonymous || showAuthor === false
    let src

    if (!isSearchSection && owner) {
      linkProps = {
        href: '/users/profile/[slug]',
        as: `/users/profile/${owner.id}`,
      }
    }

    if (isAdmin) {
      name = owner?.firstName + ' ' + owner?.lastName
      src = owner?.photoUrl || ''
    } else {
      src = !owner || isAnonymous ? '/images/emptyAvatarUser.png' : owner?.photoUrl || ''
      name = isAnonymous ? anonymousName : owner?.firstName + ' ' + owner?.lastName

      if (isSearchSection && owner && slug) {
        linkProps = {
          href: '/organizations/[slug]',
          as: `/organizations/${slug}?author_id=${owner.id}`,
        }
      }
    }

    Avatar = <UserAvatar src={src} name={name} />
  }

  const isNegative = useMemo(() => {
    const nodes = value?.votesByReasonMessageId?.nodes
    if (nodes?.length > 0) return nodes[0]?.positive === false
    return false
  }, [value.votesByReasonMessageId])

  return (
    <ConditionalLink
      condition={!!(owner || fromRedacted)}
      pathname={routes.PublicEmails.href}
      className="text-decoration-none"
      {...linkProps}
    >
      <Media className="d-flex align-items-center">
        {React.cloneElement(Avatar, {
          className: `mr-2 text-white overflow-hidden d-lg-block ${!isRelated ? 'd-none' : ''}`,
          size,
        })}
        <Media.Body>
          <div className="d-flex">
            {!isRelated && fromRedacted ? (
              <div className="font-weight-bold text-dark">
                <span className="d-lg-none">{fromLabel} </span>
                {name}
              </div>
            ) : (
              <strong className="mr-2 font-weight-bold text-dark">{name}</strong>
            )}
            {isAdmin && isAnonymous && (
              <em className="text-muted d-inline-block mr-1">(As Anonymous)</em>
            )}
            {isNegative && (
              <Badge className="my-auto">
                <FontAwesomeIcon icon={faChevronDown} color="red" className="mr-1" />
                <span className="text-muted text-uppercase">{opposedTag}</span>
              </Badge>
            )}
          </div>
          {createdDate && (
            <small className="text-uppercase text-muted">
              {formatISOdate(createdDate, 'MMM d, yyyy')}
            </small>
          )}
        </Media.Body>
      </Media>
    </ConditionalLink>
  )
}

UserMedia.propTypes = {
  value: PropTypes.oneOfType([EmailPropType, MessagePropType]).isRequired,
  anonymousName: PropTypes.string.isRequired,
  fromLabel: PropTypes.string.isRequired,
  opposedTag: PropTypes.string.isRequired,
  asAnonymous: PropTypes.bool,
  isAdmin: PropTypes.bool,
  isRelated: PropTypes.bool,
  isSearchSection: PropTypes.bool,
  size: PropTypes.number,
}

UserMedia.defaultProps = {
  asAnonymous: false,
  isAdmin: false,
  isRelated: false,
  isSearchSection: false,
  size: 40,
}

export default withLocale('common', 'userMedia')(UserMedia)
